import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { PenTool } from 'lucide-react';

/**
 * Acknowledgement block for the PPE Issue modal. Self-contained UI for
 * choosing how the employee acknowledges receipt (signature pad, typed
 * name, or issuer confirmation). The parent owns all state — this
 * component is pure presentation + event forwarding.
 */
export function PPEAcknowledgementBlock({
  acknowledgementType,
  onChangeType,
  signatureCanvasRef,
  onStartDrawing,
  onDraw,
  onStopDrawing,
  onClearSignature,
  typedAck,
  onChangeTypedAck,
  issuerConfirmed,
  onChangeIssuerConfirmed,
}) {
  return (
    <div className="border-t border-border pt-4">
      <Label className="text-base font-semibold">Employee Acknowledgement *</Label>
      <div className="mt-3 space-y-3">
        <div className="flex gap-4">
          <Button
            type="button"
            variant={acknowledgementType === 'signature' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChangeType('signature')}
          >
            <PenTool className="w-4 h-4 mr-2" />
            Signature
          </Button>
          <Button
            type="button"
            variant={acknowledgementType === 'typed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChangeType('typed')}
          >
            Typed
          </Button>
          <Button
            type="button"
            variant={acknowledgementType === 'issuer_confirmation' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChangeType('issuer_confirmation')}
          >
            Issuer Confirms
          </Button>
        </div>

        {acknowledgementType === 'signature' && (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Draw signature below:</p>
            <div className="relative">
              <canvas
                ref={signatureCanvasRef}
                width={400}
                height={150}
                className="border border-border rounded-lg bg-white cursor-crosshair w-full"
                onMouseDown={onStartDrawing}
                onMouseMove={onDraw}
                onMouseUp={onStopDrawing}
                onMouseLeave={onStopDrawing}
                onTouchStart={onStartDrawing}
                onTouchMove={onDraw}
                onTouchEnd={onStopDrawing}
                data-testid="signature-canvas"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2"
                onClick={onClearSignature}
              >
                Clear
              </Button>
            </div>
          </div>
        )}

        {acknowledgementType === 'typed' && (
          <div className="space-y-2">
            <Input
              value={typedAck}
              onChange={(e) => onChangeTypedAck(e.target.value)}
              placeholder="Employee types their name to acknowledge receipt"
              data-testid="input-typed-ack"
            />
            <p className="text-xs text-muted-foreground">
              By typing their name, the employee acknowledges receipt of the PPE.
            </p>
          </div>
        )}

        {acknowledgementType === 'issuer_confirmation' && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="issuer_confirm"
              checked={issuerConfirmed}
              onCheckedChange={onChangeIssuerConfirmed}
              data-testid="issuer-confirm-checkbox"
            />
            <Label htmlFor="issuer_confirm" className="text-sm">
              I confirm that I have issued this PPE to the employee and they have acknowledged receipt.
            </Label>
          </div>
        )}
      </div>
    </div>
  );
}
